import { clearAllCache } from '@/lib/cache'
import { clearDrafts } from '@/lib/drafts/store'
import { limparAcesso } from './acesso'
import { marcarAvisoDeLogin, type MotivoSaida } from './avisoDeLogin'
import { publicarNoCanal } from './canalEntreAbas'
import {
  estaAbaEstaSaindo,
  gravarMotivoSaida,
  lerMotivoGravado,
  lerMotivoSaida,
  marcarSaidaDestaAba,
  marcarSaidaPendente,
  retirarOperacao,
} from './sessaoEntreAbas'
import { clearAuthStorage } from './storage'

export interface OpcoesDeEncerramento {
  motivo: MotivoSaida
  /** Dono do rascunho. Ausente → `user_id` do localStorage, lido ANTES do `clearAuthStorage`. */
  userId?: string | null
  /** Aviso na tela de login DESTA aba. "Sair" voluntário não mostra: quem clicou sabe. */
  avisarNestaAba: boolean
  /** O back não confirmou o "Sair" (rede, 503, teto): grava `auth:saida_pendente` (faixa 25 §6, D5). */
  saidaPendente?: boolean
}

/**
 * A saída local da sessão — a MESMA para "Sair", sessão morta, corte do servidor e troca de senha com sessão
 * encerrada (I9). Não mexe no socket: quem chama derruba antes (`disconnectSocket`).
 */
export function encerrarSessaoLocal({ motivo, userId, avisarNestaAba, saidaPendente = false }: OpcoesDeEncerramento): void {
  // PRIMEIRO: `retirarOperacao` lá embaixo acorda quem espera nesta aba; com a marca, ninguém decide nada (I8).
  marcarSaidaDestaAba()
  // O access some da memória: `compararAcesso` passa a dizer `'sumiu'` = esta aba saiu (faixa 25 §10).
  limparAcesso()
  // Rascunho é conversa em texto CLARO no localStorage. Vem ANTES do clearAuthStorage, que apaga o `user_id`.
  // Preferência (`prefs:<user_id>`) fica de propósito — é ajuste de máquina, não texto de cliente.
  clearDrafts(userId ?? localStorage.getItem('user_id'))
  void clearAllCache()
  gravarMotivoSaida(motivo)
  // A marca vem DEPOIS do motivo: a aba congelada que acorda pelo `storage` da marca lê o motivo certo.
  if (saidaPendente) marcarSaidaPendente()
  clearAuthStorage()
  if (avisarNestaAba) marcarAvisoDeLogin(motivo)
  // Sinal às outras abas (faixa 25 §6.4): saem com o motivo SEM esperar 401.
  publicarNoCanal({ tipo: 'saiu', motivo })
  // Anúncio POR ÚLTIMO (D5): quem espera acorda com tudo já limpo e o motivo gravado.
  retirarOperacao()
  // 🔴 Nada aqui pode causar RE-RENDER. `window.location.assign` não interrompe o React: o documento só troca
  // centenas de ms depois, e a árvore privada continua viva e renderizando esse vão inteiro. `queryClient.clear()`
  // e `setUser(null)` já estiveram no logout e eram o defeito: o clear chama `remove()` → `query.destroy()`, que
  // NÃO faz `reset()`; query com `placeholderData: keepPreviousData` reentrega o dado anterior pelo
  // `#lastQueryWithDefinedData` (vive no observer), enquanto a query pura vai a `undefined` — tela com metade dos
  // dados de antes e metade zerada, e o primeiro cruzamento de duas queries lançava em render. A navegação abaixo
  // é COMPLETA: o QueryClient em memória morre com o documento, e quem entra limpa incondicionalmente
  // (`queryClient.clear()` no `handleLogin` de `login/page.tsx`). Completa também zera o Router Cache do App
  // Router, que sobrevive a navegação soft.
  window.location.assign('/login')
}

/**
 * Outra aba saiu (mensagem `saiu` do canal, ou a marca de saída pendente vista pelo `storage`): esta só navega,
 * com o aviso do motivo. Não limpa o storage compartilhado nem sinaliza — a outra já fez.
 */
export function seguirSaidaDeOutraAba(motivo: MotivoSaida = lerMotivoSaida()): void {
  marcarSaidaDestaAba()
  limparAcesso()
  marcarAvisoDeLogin(motivo)
  window.location.assign('/login')
}

/**
 * A renovação disse `sessao-morta`/`saida-pendente` numa aba ABERTA (interceptor, agendador, socket). Se outra
 * aba já sinalizou saída (motivo gravado desde o último login), segue o motivo dela (I8); senão é "sessão
 * encerrada", com a limpeza completa. A carga inicial NÃO passa aqui (vai ao `/login` sem aviso — portão).
 */
export function sairPorSessaoMorta(): void {
  if (estaAbaEstaSaindo()) return
  const deOutraAba = lerMotivoGravado()
  if (deOutraAba !== null) {
    seguirSaidaDeOutraAba(deOutraAba)
    return
  }
  encerrarSessaoLocal({ motivo: 'encerrada', avisarNestaAba: true })
}
